import { CAST_AI } from '@cast/constants';

import {
  AgentCostSetup,
  AgentAutoscalerSetup,
  AgentWorkloadAutoscalerSetup,
  AgentReliabilitySetup,
} from 'hooks/dialogs/cluster/components/features';

import { useIsReliabilityMetricsAvailableInBeta } from 'hooks/permissions/useIsReliabilityMetricsAvailableInBeta';
import { CastctlFeatureSelection } from './types';

import { Typography, Box, Stack } from '@cast/design-system';

type Props = {
  featureSelection: CastctlFeatureSelection;
  onFeatureChange: (params: {
    feature: keyof CastctlFeatureSelection;
    enabled: boolean;
  }) => void;
};

export const FeaturesSection = ({
  featureSelection,
  onFeatureChange,
}: Props) => {
  const isReliabilityMetricsAvailableInBeta =
    useIsReliabilityMetricsAvailableInBeta();

  return (
    <Stack gap={4}>
      <Typography variant="P12R" color="textSecondary">
        Choose what {CAST_AI} features castctl enables on your cluster. You can
        change this anytime after the initial setup.
      </Typography>

      <Stack data-testid="castctl-features-container">
        <Stack>
          <AgentCostSetup checked readonly />
          {isReliabilityMetricsAvailableInBeta ? (
            <Box pl={32}>
              <AgentReliabilitySetup
                checked={featureSelection.reliabilityMetrics}
                showVersionAlert={featureSelection.reliabilityMetrics}
                onChange={(enabled) =>
                  onFeatureChange({ feature: 'reliabilityMetrics', enabled })
                }
              />
            </Box>
          ) : null}
        </Stack>
        <AgentAutoscalerSetup
          checked={featureSelection.autoscaler}
          descriptionOverride={`Optimize cluster performance and reduce costs with intelligent automated scaling. ${CAST_AI} offers Karpenter-based autoscaling for EKS.`}
          onChange={(enabled) =>
            onFeatureChange({ feature: 'autoscaler', enabled })
          }
        />
        <AgentWorkloadAutoscalerSetup
          checked={featureSelection.workloadAutoscaler}
          onChange={(enabled) =>
            onFeatureChange({ feature: 'workloadAutoscaler', enabled })
          }
        />
      </Stack>
    </Stack>
  );
};
